import { useRef, useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import ChartHeader from './ChartHeader';
import { ChartInteractionHandler, createChartAnimations, createHoverEffects } from '../../utils/chartUtils';

function ChartContainer({ 
  title,
  subtitle,
  children,
  loading = false,
  onDrillDown,
  onReset,
  chartRef,
  chartData,
  showDrillDownNavigation = false,
  enableAnimations = true,
  className = "",
  style = {}
}) {
  const containerRef = useRef();
  const [interactionHandler] = useState(() => new ChartInteractionHandler(chartRef));
  const [drillDownLevel, setDrillDownLevel] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle drill-down navigation
  const handleNavigateBack = () => {
    if (interactionHandler.getCurrentLevel() > 0) {
      const previousData = interactionHandler.navigateBack();
      setDrillDownLevel(interactionHandler.getCurrentLevel());
      
      if (onReset && previousData) {
        setIsAnimating(true);
        onReset(previousData);
        
        // Reset animation state after transition
        setTimeout(() => {
          setIsAnimating(false);
        }, 600);
      }
    }
  };

  const handleResetToRoot = () => {
    const rootData = interactionHandler.resetToRoot();
    setDrillDownLevel(0);
    
    if (onReset && rootData) {
      setIsAnimating(true);
      onReset(rootData);
      
      setTimeout(() => {
        setIsAnimating(false);
      }, 600);
    }
  };

  // Handle drill-down from chart interactions
  const handleChartDrillDown = (elementIndex, datasetIndex, currentData) => {
    if (onDrillDown) {
      const drillDownData = onDrillDown(elementIndex, datasetIndex, currentData);
      
      if (drillDownData) {
        // Store the drill-down in the interaction handler
        interactionHandler.drillDownStack.push({
          data: currentData,
          elementIndex,
          datasetIndex,
          timestamp: Date.now()
        });
        
        setDrillDownLevel(interactionHandler.getCurrentLevel());
        setIsAnimating(true);
        
        setTimeout(() => {
          setIsAnimating(false);
        }, 600);
        
        return drillDownData;
      }
    }
    return null;
  };

  // Apply animations to container
  useEffect(() => {
    if (enableAnimations && containerRef.current) {
      const container = containerRef.current;
      
      // Entry animation
      container.style.opacity = '0';
      container.style.transform = 'translateY(20px)';
      container.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
      
      // Trigger animation
      requestAnimationFrame(() => {
        container.style.opacity = '1';
        container.style.transform = 'translateY(0)';
      });
    }
  }, [enableAnimations]);

  // Apply loading animation
  useEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      
      if (loading) {
        container.style.opacity = '0.6';
        container.style.pointerEvents = 'none';
        container.style.transition = 'opacity 0.3s ease-in-out';
      } else {
        container.style.opacity = '1';
        container.style.pointerEvents = 'auto';
      }
    }
  }, [loading]);

  // Apply data update animation
  useEffect(() => {
    if (isAnimating && containerRef.current) {
      const container = containerRef.current;
      container.style.transition = 'transform 0.3s ease-in-out';
      container.style.transform = 'scale(0.98)';
      
      setTimeout(() => {
        container.style.transform = 'scale(1)';
      }, 150);
    }
  }, [isAnimating]);

  const containerStyle = {
    position: 'relative',
    ...style
  };

  return (
    <div 
      ref={containerRef}
      className={`chart-container ${className}`}
      style={containerStyle}
    >
      {/* Chart Header with Controls */}
      <ChartHeader
        title={title}
        subtitle={subtitle}
        chartRef={chartRef}
        chartData={chartData}
        onReset={drillDownLevel > 0 ? handleResetToRoot : undefined}
        showResetButton={drillDownLevel > 0}
        containerRef={containerRef}
      />

      {/* Drill-down Navigation */}
      {showDrillDownNavigation && drillDownLevel > 0 && (
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={handleNavigateBack}
            className="flex items-center gap-1 px-3 py-1 rounded text-sm hover:opacity-80 transition-opacity"
            style={{ 
              background: 'var(--primary)', 
              color: 'white' 
            }}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          
          <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--muted)' }}>
            <span>Level {drillDownLevel + 1}</span>
            {drillDownLevel > 0 && (
              <>
                <span>•</span>
                <button
                  onClick={handleResetToRoot}
                  className="text-sm hover:opacity-80 transition-opacity"
                  style={{ color: 'var(--primary)' }}
                >
                  Reset to Root
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg z-10" style={{ background: 'rgba(var(--panel-rgb, 255,255,255), 0.8)' }}>
          <div className="flex items-center gap-2">
            <div 
              className="animate-spin rounded-full h-6 w-6 border-b-2"
              style={{ borderColor: 'var(--primary)' }}
            />
            <span className="text-sm" style={{ color: 'var(--muted)' }}>
              Loading chart...
            </span>
          </div>
        </div>
      )}

      {/* Chart Content */}
      <div className="chart-content">
        {children}
      </div>

      {/* Animation Overlay for Smooth Transitions */}
      {isAnimating && (
        <div 
          className="absolute inset-0 pointer-events-none rounded-lg"
          style={{
            background: 'linear-gradient(45deg, transparent 30%, rgba(59, 130, 246, 0.1) 50%, transparent 70%)',
            animation: 'shimmer 0.6s ease-in-out'
          }}
        />
      )}

      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .chart-container {
          transition: all 0.3s ease-in-out;
        }
        
        .chart-container:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .chart-content {
          position: relative;
          overflow: hidden;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
}

export default ChartContainer;